import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { isAlive, uniqueId } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

export interface Args {
	text: string;
	onTextChange: (text: string) => void;
	placeholder?: string;
	disabled?: boolean;
	options?: { theme?: string; mode?: string };
	updateEditor?: boolean; // changed from parent when forcefully changing text
	resetUpdateEditor?: () => void;
}

const DefaultOptions = {
	theme: 'code_dark',
	mode: 'json',
};

export default class FormCustomAceEditorComponent<T extends Args> extends Component<T> {
	uId = `custom-ace-editor-${uniqueId()}`;
	editor: AceEditor | null = null;

	@tracked isValid = true;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		next(this, this.checkPlaceholder);
	}

	get options() {
		// possibly extended
		return this.args.options || {};
	}

	outputChanged() {
		if (isAlive(this)) {
			const text = this.editor?.getSession().getValue() || '';
			this.args.onTextChange(text);
		}
	}

	checkPlaceholder() {
		if (!this.args.text && this.args.placeholder) {
			this.args.onTextChange(this.args.placeholder);
		}
	}

	@action
	inputChangedFromOutside() {
		if (this.args.updateEditor) {
			const editor = this.editor;
			if (editor && editor.getSession().getValue() !== this.args.text) {
				editor.getSession().setValue(this.args.text, -1);
			}
			this.args.resetUpdateEditor?.();
		}
	}

	@action
	disabledChanged() {
		if (this.editor) {
			this.editor.setReadOnly(!!this.args.disabled);
		}
	}

	@action
	setupView(_node: HTMLElement) {
		const editor = window.ace.edit(this.uId),
			session = editor.getSession(),
			options = $.extend({}, DefaultOptions, this.options);

		editor.$blockScrolling = Infinity;
		editor.setTheme('ace/theme/' + options.theme);
		session.setMode('ace/mode/' + options.mode);

		session.on('change', () => {
			this.outputChanged();
		});

		session.on('changeAnnotation', () => {
			const errors = editor
				.getSession()
				.getAnnotations()
				.filter((item) => item.type === 'error');
			if (isAlive(this)) {
				this.isValid = !errors.length;
			}
		});

		session.setUseWrapMode(true);
		session.setWrapLimitRange(null, null);

		editor.setReadOnly(!!this.args.disabled);

		editor.focus();

		this.editor = editor;

		this.inputChangedFromOutside();
	}

	@action
	teardownView() {
		// extended
	}
	<template>
		<div
			id={{this.uId}}
			class="custom-editor"
			{{didInsert this.setupView}}
			{{didUpdate this.inputChangedFromOutside @updateEditor}}
			{{didUpdate this.disabledChanged @disabled}}
			{{willDestroy this.teardownView}}
		>
			{{@text}}
		</div>
	</template>
}
